import BoleronLoaderSvg from "@icons/Boleron_logo_loader.svg";
import { useTranslation, I18nextContext } from "gatsby-plugin-react-i18next";
import React, { useEffect, useRef, useState } from "react";
import { inject, observer } from "mobx-react";

import GoogleReviewRating from "@images/testemonials/google-review-rating.svg";
import VerticalCarousel from "@components/common/VerticalCarousel";
import GoogleReviewBox from "@components/common/GoogleReviewBox";
import BoleronLoader from "@components/common/BoleronLoader";
import GlobalBanner from "@components/common/GlobalBanner";
import GoogleRating from "@icons/google-rating.svg";
import TrustpilotRating from "@icons/trustpilot-rating.svg";
import SeoTenant from "@common/SeoTenant";

import "../../styles/components/Tooltip.sass";
import "../../styles/components/Layout.sass";
import PropTypes from "prop-types";
import { navigate } from "gatsby";
import { shuffleArray } from "@utils/functions";
import { GATSBY_CONFIGURATION } from "../../config/activeProducts";
import PercentageBoleronLoader from "./PercentageBoleronLoader";
import ProductsService from "../../services/ProductsService";
import useBreakpoints from "../../hooks/useBreakpoints";
import LazySVGS from "../../images/LazySVGS";
import LayoutError from "./LayoutError";
import Header from "./Header";
import Footer from "./Footer";

const ChatbotToggleContainer = React.lazy(() => import("../chatbot/ToggleChatbotContainer"));

export const VARIANT_DEFAULT = "default";
export const VARIANT_WORKFLOW = "workflow";
export const VARIANT_DEFAULT_NO_FOOTER = "defaultNoFooter";
export const VARIANT_WORKFLOW_HIDE_BACK_BUTTON = "workflowNoBackButton";

const Layout = props => {
  const { t } = useTranslation();
  const {
    workflow,
    auth,
    title,
    isDefaultTitle,
    boxTitle,
    layoutStyles = "",
    boxTitleStyle,
    boxSubTitle,
    boxSubTitleStyle,
    testimonialsData,
    boxSubTitleHTML,
    centerContent,
    addonStyles = false,
    boxTitleIcon,
    boxTitleIconTooltip,
    dataTooltip,
    btnContinue,
    btnContinueTextSize = "is-size-3-5",
    contentBoxStyles,
    btnContinueTxt,
    continueBtnComponentStyles,
    pageContext,
    btnSubmit,
    btnDisabled,
    children,
    continueFunc,
    error,
    isWarningError,
    errorTitle,
    errorMessage,
    errorBtnTxt,
    redirectPathErrorMsg,
    redirectActionErrorMsg,
    titleTooltip,
    loading,
    otherBtn,
    isPercentageLoader,
    percentageLoaderConfig,
    erraseMarginBottom = false,
    multipleLineBoxSubTitle,
    boldedSubTitles = [],
    isExclamationSvg,
    navbarWorkflow,
    variant,
    emptyWorkflowRedirect,
    showSuccessMessage,
    onSuccessAknowledge,
    loadingParams,
    hideErrorBtn,
    workflowContainerMaxWidth,
    workflowContainerMinWidth
  } = props;

  const workflowMainRef = useRef(null);
  const [layoutTimeout, setLayoutTimeout] = useState(false);
  const [shuffledTestimonialsData, setShuffledTestimonialsData] = useState(null);
  const [pathname, setPathname] = useState("/");
  const [showChatbot, setShowChatbot] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setPathname(window.location?.pathname);
    }
  }, []);

  useEffect(() => {
    if (!workflowMainRef.current) return;
    const el = workflowMainRef.current;
    if (workflowContainerMaxWidth != null) {
      el.style.maxWidth = typeof workflowContainerMaxWidth === "number" ? `${workflowContainerMaxWidth}px` : workflowContainerMaxWidth;
      el.style.boxSizing = "border-box";
    }
    if (workflowContainerMinWidth != null) {
      el.style.minWidth = typeof workflowContainerMinWidth === "number" ? `${workflowContainerMinWidth}px` : workflowContainerMinWidth;
      el.style.boxSizing = "border-box";
    }
  }, [workflowContainerMaxWidth, workflowContainerMinWidth]);

  const languageContext = React.useContext(I18nextContext);
  const currentLanguage = languageContext?.language;

  const normalizePath = path => (path === "/" ? "/" : path.replace(/\/+$/, ""));
  const normalizedPath = normalizePath(pathname || "/");
  const excludedPaths = [
    "/my-profile-page",
    "/policies",
    "/unsubscribe-service",
    "/unsubscribe-auto-service",
    "/referral",
    "/referral-success",
    "/referral-error",
    "/policy-payment",
    "/policy-company",
    "/policy-document",
    "/policy-cancellation",
    "/payment-fail",
    "/payment-completed",
    "/message",
    "/message-simple",
    "/login-without-pass",
    "/login-with-pass",
    "/login-with-pass-success",
    "/login-with-email-link",
    "/forgotten-pass",
    "/forgotten-pass-success",
    "/discount-loyal-customer",
    "/proverka-globi-kat-grazhdanska",
    "/workflow/review",
    "/workflow/payment"
  ];

  useEffect(() => {
    if (emptyWorkflowRedirect && workflow && !workflow.flow) {
      navigate("/", { replace: true });
    }

    if (testimonialsData) {
      setShuffledTestimonialsData(shuffleArray(testimonialsData));
    }

    setTimeout(() => setLayoutTimeout(true), 600);
  }, []);

  const { isMobile, isDesktop } = useBreakpoints();
  const layoutVariant = navbarWorkflow ? VARIANT_WORKFLOW : variant || VARIANT_DEFAULT;
  const layoutTitle = title || ProductsService.getProductName(workflow?.product, currentLanguage);

  const showBackButton = variant !== VARIANT_WORKFLOW_HIDE_BACK_BUTTON && !showSuccessMessage;
  const hideHeader = typeof window !== "undefined" && window?.sessionStorage?.getItem?.("hideHeader") === "true";
  // const isStatusPage = ["400", "404", "500", "503"].includes(pageContext?.pageType);
  const shouldShowChatbot = !hideHeader && !excludedPaths.includes(normalizedPath);

  useEffect(() => {
    if (!shouldShowChatbot) {
      setShowChatbot(false);
      return undefined;
    }

    setShowChatbot(true);
  }, [shouldShowChatbot]);

  const seoNoIndexComponent = React.Children.toArray(children).find(child => React.isValidElement(child) && ["SeoNoIndex"].includes(child?.type?.name));

  const otherChildren = React.Children.toArray(children).filter(child => React.isValidElement(child) && child?.type?.name !== "SeoNoIndex");

  const content = (
    <>
      {otherChildren}
      {btnContinue && (
        <div className="has-text-centered">
          <button
            disabled={btnDisabled}
            className={`button is-medium is-info is-rounded btn-continue theme-button ${btnContinueTextSize}`}
            type={btnSubmit ? "submit" : "button"}
            style={continueBtnComponentStyles}
            onClick={continueFunc}
            data-cy-continue-btn
          >
            <span className="is-uppercase" title={btnContinueTxt || t("key240631", "Продължи")}>
              {btnContinueTxt || t("continue", "Продължи")}
            </span>
          </button>
        </div>
      )}
      {otherBtn && otherBtn}
    </>
  );

  const loadingContent = (
    <div className="is-flex is-flex-direction-column is-justify-content-center is-align-items-center loading-content-wrapper">
      <SeoTenant isLoadingSeo={true} />

      {GATSBY_CONFIGURATION === "boleron" && <BoleronLoaderSvg />}
      <div className="mt-4 has-text-centered" style={isPercentageLoader ? { width: "100%", maxWidth: 500 } : { width: "100%" }}>
        {!isPercentageLoader && <BoleronLoader />}

        {isPercentageLoader && <PercentageBoleronLoader cssDuration={percentageLoaderConfig.cssDuration} isJustLoader t={t} />}
      </div>
      <span className="mt-4 mb-4 loading-content-text">{loadingParams?.message ? loadingParams.message : t("key240641", "Моля, изчакайте...")}</span>
    </div>
  );

  const errorContent = (
    <LayoutError
      isWarningError={isWarningError}
      isExclamationSvg={isExclamationSvg}
      errorTitle={errorTitle}
      errorMessage={errorMessage}
      redirectPathErrorMsg={redirectPathErrorMsg}
      redirectActionErrorMsg={redirectActionErrorMsg}
      errorBtnTxt={errorBtnTxt}
      hideErrorBtn={hideErrorBtn}
      pageContext={pageContext}
    />
  );

  const successMessageContent = (
    <div className="has-text-centered">
      <LazySVGS svg="Success" className="success-svg" width={73} height={75} style={{ marginTop: 30, marginBottom: 30 }} />
      <p style={{ fontSize: 18 }}>{t("key240491", "Твоите промени са")}</p>
      <div className="success-text mb-4">{t("key24050", "Запазени успешно!")}</div>
      <div className="has-text-centered">
        <button
          type="button"
          className="button is-info is-rounded success-message-btn"
          onClick={() => {
            if (onSuccessAknowledge) {
              onSuccessAknowledge();
            }
          }}
        >
          <span className="is-uppercase">{t("key24051", "ОК")}</span>
        </button>
      </div>
      <button
        className="btn-home-on-success"
        type="button"
        onClick={() => {
          navigate("/");
        }}
      >
        <p className="is-uppercase is-regular-font-family">{t("key24052", "Начало")}</p>
      </button>
    </div>
  );

  const renderDefault = () => (
    <>
      {hideHeader && (props.heroBodyMobileApp ? props.heroBodyMobileApp : null)}
      {!hideHeader && <GlobalBanner />}
      {!hideHeader && (
        <Header body={props.heroBody} auth={auth} hideLogoText={props.hideLogoText} marginBottom={props.marginBottom} navbarWorkflow={props.navbarWorkflow} siteTitle="Home" />
      )}

      <main
        className={`${props.noMainPadding ? "p-0" : "p-3"} ${layoutStyles}`}
        style={{
          marginTop: props.mt ? -50 : 0
        }}
      >
        {otherChildren}
      </main>
      {props.variant !== VARIANT_DEFAULT_NO_FOOTER && <Footer />}
    </>
  );

  const renderWorkflow = () => {
    const boxContentContainerPrefix = addonStyles && addonStyles === "fine-check-result" ? `${addonStyles}-` : "";
    const layoutMaxHeight = addonStyles && addonStyles === "car-damage" ? `${addonStyles}-` : "";
    const layoutContentGap = addonStyles && addonStyles === "message-simple" ? `row-gap-24` : "";

    const workflowContainerMaxWidthStyle = workflowContainerMaxWidth != null ? { maxWidth: workflowContainerMaxWidth } : {};
    const workflowContainerMinWidthStyle = workflowContainerMinWidth != null ? { minWidth: workflowContainerMinWidth } : {};
    const workflowContainerBoxSizingStyle = workflowContainerMaxWidth != null || workflowContainerMinWidth != null ? { boxSizing: "border-box" } : {};

    return (
      <>
        {hideHeader ? (
          <div style={{ marginTop: 180 }} />
        ) : (
          <Header siteTitle="Boleron" auth={auth} title={isDefaultTitle ? "" : layoutTitle} navbarWorkflow={navbarWorkflow} showBackButton={showBackButton} isWorkflow />
        )}
        <main
          ref={workflowMainRef}
          className={`box-container ${layoutStyles}`}
          style={{
            position: "relative",
            ...(erraseMarginBottom ? { marginBottom: 0 } : {}),
            ...workflowContainerMaxWidthStyle,
            ...workflowContainerMinWidthStyle,
            ...workflowContainerBoxSizingStyle
          }}
        >
          <div
            className={`${layoutMaxHeight}layout-workflow-border-radius boleron-box-margin ${hideHeader ? "min-height-95-percent" : ""}`}
            style={{
              ...(erraseMarginBottom ? { marginBottom: 0 } : {}),
              ...(layoutContentGap ? { display: "flex" } : {}),
              ...(isDesktop && testimonialsData ? { minHeight: 915 } : {})
            }}
          >
            <div className="workflow-inner-fade">
              {showSuccessMessage ? (
                successMessageContent
              ) : (
                <>
                  {loading && layoutTimeout && loadingContent}

                  {error && errorContent}

                  {!loading && !error && (
                    <>
                      {GATSBY_CONFIGURATION === "boleron" &&
                        !["GO", "CSC", "TRA"].includes(workflow.product) &&
                        (boxTitle === t("key240661", "Оферти") || (boxTitle === t("key24067", "Застрахователна сума") && !["CSC", "CSC+GO"].includes(workflow.product))) && (
                          <div
                            className={`is-flex ${isMobile ? "is-justify-content-center" : "is-justify-content-space-between"}`}
                            style={{ gap: isMobile ? 24 : 16, marginLeft: isMobile ? 0 : 25, marginRight: isMobile ? 0 : 25 }}
                          >
                            <GoogleRating style={isMobile ? { width: 110, height: 41 } : { minWidth: 146, minHeight: 43 }} />
                            <TrustpilotRating style={isMobile ? { width: 110, height: 41 } : { minWidth: 146, minHeight: 43 }} />
                          </div>
                        )}

                      {boxTitle && !boxTitleIconTooltip && (
                        <h1 className="has-text-centered has-text-primary mt-5 boxTitle" style={boxTitleStyle}>
                          {boxTitleIcon}
                          {boxTitle}
                          {titleTooltip}
                        </h1>
                      )}

                      {boxTitle && boxTitleIconTooltip && (
                        <div className="box-title-tooltip-container">
                          <h1 className="has-text-centered has-text-primary mt-5 boxTitle">{boxTitle}</h1>
                          <div data-tooltip={dataTooltip} className="has-tooltip-multiline has-tooltip-left">
                            {boxTitleIconTooltip}
                          </div>
                        </div>
                      )}

                      {boxSubTitle && multipleLineBoxSubTitle ? (
                        <p className="light-text is-size-4 box-subtitle multiline">
                          {boxSubTitle.map((subTitle, index) => (
                            <React.Fragment key={`${subTitle}-${index}`}>
                              <span className={boldedSubTitles.includes(index) ? "is-bold-family" : ""}>{subTitle}</span> <br />
                            </React.Fragment>
                          ))}
                        </p>
                      ) : (
                        boxSubTitle && (
                          <p className="light-text is-size-4 box-subtitle" style={boxSubTitleStyle}>
                            {boxSubTitle}
                          </p>
                        )
                      )}

                      {centerContent ? (
                        <div
                          className={`is-flex is-flex-direction-column is-justify-content-center is-align-items-center box-content-container ${layoutContentGap}`}
                          style={contentBoxStyles}
                        >
                          {!!boxSubTitleHTML && boxSubTitleHTML}
                          {content}
                        </div>
                      ) : (
                        <div
                          className={`is-flex is-flex-direction-column is-justify-content-center is-align-items-center ${boxContentContainerPrefix}box-content-container`}
                          style={contentBoxStyles}
                        >
                          {!!boxSubTitleHTML && boxSubTitleHTML}
                          {content}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            {GATSBY_CONFIGURATION === "boleron" && isMobile && shuffledTestimonialsData && (
              <div className="is-fullwidth">
                <p className="is-regular-font-family is-size-6 has-text-centered">
                  <span className="is-size-5 is-primary-color is-bold-family">50 000+ </span> {t("customersChoseBoleron", "клиенти избраха Болерон")}
                </p>

                <GoogleReviewRating className="is-fullwidth mx-auto" />

                <VerticalCarousel arrows={false} slidesToShow={1}>
                  {shuffledTestimonialsData.map((data, index) => (
                    <GoogleReviewBox
                      key={index}
                      authorTitle={data.authorTitle}
                      authorImage={data.authorImage}
                      reviewText={data.reviewText}
                      currentLanguage={currentLanguage}
                      starType="gold"
                      styleType="vertical"
                    />
                  ))}
                </VerticalCarousel>
              </div>
            )}
          </div>

          {GATSBY_CONFIGURATION === "boleron" && isDesktop && shuffledTestimonialsData && (
            <div className="offers-testimonials-container workflow-inner-fade">
              <div className="google-rating-box">
                <p className="is-regular-font-family is-size-6">
                  <span className="is-size-5 is-primary-color is-bold-family">50 000+ </span> {t("customersChoseBoleron", "клиенти избраха Болерон")}
                </p>

                <GoogleReviewRating />
              </div>

              <VerticalCarousel arrows={false} slidesToShow={3}>
                {shuffledTestimonialsData.map((data, index) => (
                  <GoogleReviewBox
                    key={index}
                    authorTitle={data.authorTitle}
                    authorImage={data.authorImage}
                    reviewText={data.reviewText}
                    currentLanguage={currentLanguage}
                    starType="gold"
                    styleType="vertical"
                  />
                ))}
              </VerticalCarousel>
            </div>
          )}
        </main>
      </>
    );
  };

  return (
    <>
      {shouldShowChatbot && showChatbot && (
        <React.Suspense fallback={null}>
          <ChatbotToggleContainer />
        </React.Suspense>
      )}
      {seoNoIndexComponent}
      {layoutVariant === VARIANT_WORKFLOW && renderWorkflow()}
      {layoutVariant === VARIANT_WORKFLOW_HIDE_BACK_BUTTON && renderWorkflow()}
      {layoutVariant === VARIANT_DEFAULT && renderDefault()}
      {layoutVariant === VARIANT_DEFAULT_NO_FOOTER && renderDefault({ hideFooter: true })}
      {GATSBY_CONFIGURATION === "vivacom" && (
        <a
          onClick={() => {
            window.Cookiebot.show();
          }}
          id="open-cookie-cons"
          style={{
            position: "fixed",
            left: 0,
            bottom: -5,
            opacity: 0.5,
            zIndex: 20
          }}
        >
          <img src="/images/vivacom/cookie-open.png" alt="Open cookie bot settings" style={{ width: 50 }} />{" "}
        </a>
      )}
    </>
  );
};

Layout.defaultProps = {
  children: <div />
};

Layout.propTypes = {
  children: PropTypes.node
};

export default inject("auth", "workflow")(observer(Layout));
